import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ZSpinner } from '@marageti/z4-lib';
import { AnyEntityVersion, EntityType } from '../../types';
import { AttributeDiff } from '../use-version-diff';
import DiffAccordion from './diff-accordion';
import { formatDateSafe } from '../../audit-utils';
import { DIFF_COLORS } from '../../constants';

interface VersionComparisonViewProps {
  currentVersion: AnyEntityVersion | null;
  previousVersion: AnyEntityVersion | null;
  entityType: EntityType;
  entityId: string;
  currentIndex: number;
  totalVersions: number;
  diffs: AttributeDiff[];
  isLoading: boolean;
  error: Error | null;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  showUnchanged: boolean;
  onToggleUnchanged: () => void;
}

const VersionComparisonView: React.FC<VersionComparisonViewProps> = ({
  currentVersion,
  previousVersion,
  entityType,
  entityId,
  currentIndex,
  totalVersions,
  diffs,
  isLoading,
  error,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  showUnchanged,
  onToggleUnchanged,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const allKeys = useMemo(() => diffs.map((d) => d.key), [diffs]);

  useEffect(() => {
    setExpandedKeys(new Set(diffs.filter((d) => d.hasChanges).map((d) => d.key)));
  }, [diffs]);

  const handleToggle = useCallback((key: string, isExpanded: boolean) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedKeys(new Set(allKeys));
  }, [allKeys]);

  const handleCollapseAll = useCallback(() => {
    setExpandedKeys(new Set());
  }, []);

  const allExpanded = allKeys.length > 0 && expandedKeys.size === allKeys.length;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <ZSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error">
        Error:
        {' '}
        {error.message}
      </Typography>
    );
  }

  if (!currentVersion) {
    return (
      <Typography color="text.secondary">
        No version selected
      </Typography>
    );
  }

  const changedCount = diffs.filter((d) => d.hasChanges).length;

  return (
    <Box>
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: 'grey.100',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={onPrevious}
              disabled={!hasPrevious}
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Version
                {' '}
                {currentIndex + 1}
                {' '}
                of
                {' '}
                {totalVersions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {entityType}
                {' '}
                /
                {entityId}
              </Typography>
            </Box>

            <IconButton
              onClick={onNext}
              disabled={!hasNext}
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {previousVersion && (
              <>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={showUnchanged}
                      onChange={onToggleUnchanged}
                      size="small"
                    />
                  )}
                  label={<Typography variant="body2">Show unchanged</Typography>}
                  sx={{ mr: 0 }}
                />
                <Button
                  size="small"
                  variant="text"
                  startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                  onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                  sx={{ textTransform: 'none', mr: 1 }}
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </Button>
                <Chip
                  label={`${changedCount} attribute${changedCount !== 1 ? 's' : ''} changed`}
                  size="small"
                  sx={{
                    backgroundColor: changedCount > 0 ? DIFF_COLORS.chipModified.bg : 'grey.200',
                    color: changedCount > 0 ? DIFF_COLORS.chipModified.text : 'text.secondary',
                  }}
                />
              </>
            )}
          </Box>
        </Box>

        {previousVersion ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Previous Version
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {previousVersion.updateId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateSafe(previousVersion.updatedAt)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', alignSelf: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                vs
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Current Version
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {currentVersion.updateId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDateSafe(currentVersion.updatedAt)}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Paper>

      {(() => {
        if (!previousVersion) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                First Version (No previous version to compare)
              </Typography>
              <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  This is the first version of this entity. There is no previous version to compare against.
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 500,
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, fontSize: '12px', fontFamily: 'monospace' }}>
                    {JSON.stringify(currentVersion, null, 2)}
                  </pre>
                </Box>
              </Paper>
            </Box>
          );
        }

        if (diffs.length === 0) {
          return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No differences found between versions
              </Typography>
            </Paper>
          );
        }

        return (
          <Box>
            {diffs.map((diff) => (
              <DiffAccordion
                key={`${diff.key}-${currentVersion.updateId}`}
                diff={diff}
                expanded={expandedKeys.has(diff.key)}
                onToggle={handleToggle}
              />
            ))}
          </Box>
        );
      })()}
    </Box>
  );
};

export default VersionComparisonView;
